/**
 * ErrorBoundary.global.tsx — Fallback pleine page pour Sentry.ErrorBoundary global.
 *
 * Rendu lorsqu'une erreur non capturée fait crasher toute l'application.
 * SPEC-2026-06-08-sentry-sdk-integration §4 (frontend).
 *
 * §12 terdecies — AUCUN dialog natif (window.alert/confirm) — tout via primitives DA.
 * §11 §18 — COMPOSE les primitives DA réelles de daComponents.jsx + tokens de theme.js.
 * §12 quinquies — bords arrondis : DACard 1rem / PrimaryButton 0.625rem.
 * §12 undecies — casse grammaticale (pas d'UPPERCASE forcé).
 */

import React from 'react';
import styled from 'styled-components';
import { DACard, PrimaryButton } from './ui/daComponents';
import { COLORS, FONTS, SPACE } from './ui/theme';

/* ─── Wrapper pleine page centré ─────────────────────────────────────────────── */
const FullPageWrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.bg};
  font-family: ${FONTS.body};
  padding: ${SPACE.xxl};
`;

/* ─── Card d'erreur (compose la primitive DACard) ────────────────────────────── */
const ErrorCard = styled(DACard)`
  max-width: 28rem;
  width: 100%;
  padding: clamp(2rem, 5vw, 2.5rem) clamp(1.5rem, 4vw, 2rem);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE.lg};
`;

/* ─── Icône d'alerte (SVG inline — §12 terdecies : pas d'icon font externe) ─── */
const AlertIcon = () => (
  <svg
    width="3rem"
    height="3rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke={COLORS.danger}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/* ─── Textes ─────────────────────────────────────────────────────────────────── */
const Title = styled.h1`
  font-family: ${FONTS.head};
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  font-weight: 900;
  color: ${COLORS.ink};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${COLORS.muted};
  margin: 0;
  line-height: 1.5;
`;

/* ─── Composant ──────────────────────────────────────────────────────────────── */

/**
 * SentryGlobalErrorFallback
 *
 * Passé en prop `fallback` à `<Sentry.ErrorBoundary>` dans src/index.tsx.
 * Affiché quand l'ensemble de l'application crashe (erreur non capturée en amont
 * des ErrorBoundary scoped comme ErrorBoundary.jsx du tile-map).
 *
 * The "Reload the page" button calls window.location.reload() —
 * seul usage autorisé (reset de l'état applicatif complet), pas un dialog natif.
 */
const SentryGlobalErrorFallback: React.FC = () => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <FullPageWrap role="alert" aria-live="assertive">
      <ErrorCard>
        <AlertIcon />

        <Title>Something went wrong</Title>

        <Subtitle>
          The application ran into a problem. Our team has been notified automatically. Reload the page to continue.
        </Subtitle>

        <PrimaryButton type="button" onClick={handleReload}>
          Reload the page
        </PrimaryButton>
      </ErrorCard>
    </FullPageWrap>
  );
};

export default SentryGlobalErrorFallback;
